<template>
  <section class="lx-section" :class="{ 'lx-section--closed': !open }">
    <button type="button" class="lx-section__head" :aria-expanded="open" aria-controls="mtf-perf-body" @click="open = !open">
      <span class="lx-section__title">Model performance</span>
      <span class="lx-section__meta">
        <template v-if="status === 'loading'">loading…</template>
        <template v-else-if="doc">{{ doc.meta.country }} · {{ doc.meta.modelVersion || "model version not provided" }}</template>
        <template v-else>not available</template>
        <span class="ml-3">{{ open ? "−" : "+" }}</span>
      </span>
    </button>

    <div v-show="open" id="mtf-perf-body" class="lx-section__body">
      <div v-if="status === 'loading'" class="lx-skel-grid" aria-busy="true">
        <div class="lx-skel lx-skel--row"></div>
        <div class="lx-skel lx-skel--row"></div>
      </div>

      <div v-else-if="!doc" class="lx-state lx-state--partial" role="status">
        <span class="lx-state__title">Metrics not available for this model yet</span>
        <p class="lx-state__msg">{{ reason || "The backend did not provide model diagnostics for this country." }}</p>
      </div>

      <template v-else>
        <div class="lx-toolbar pt-0">
          <div class="lx-seg" role="tablist" aria-label="Series">
            <button v-for="s in seriesList" :key="s" type="button" role="tab" class="lx-seg__btn" :class="{ 'lx-seg__btn--on': s === sel }" :aria-selected="s === sel" @click="sel = s">{{ shortName(s) }}</button>
          </div>
          <span v-if="doc.meta.illustrative" class="lx-badge lx-badge--illustrative"><span class="lx-badge__k">Source</span><span class="lx-badge__v">fixture · real holdout where stated</span></span>
        </div>

        <div class="lx-two-col mt-3">
          <!-- 8.1 In-sample -->
          <div>
            <span class="kicker mb-2">In-sample diagnostics</span>
            <table class="lx-mtable" aria-label="In-sample diagnostics">
              <thead><tr><th scope="col">Statistic</th><th scope="col" class="num">Value</th></tr></thead>
              <tbody>
                <tr v-for="k in inSampleKeys" :key="k">
                  <td>{{ help(k).label }}<span class="lx-help" :title="help(k).help" :aria-label="help(k).help" tabindex="0">?</span></td>
                  <td class="num"><template v-if="inSample[k] != null">{{ fmtNum(inSample[k], k) }}</template><span v-else class="na" :title="why('inSample')">n/a</span></td>
                </tr>
                <tr><td>Specification</td><td class="num">{{ eq ? eq.spec : "n/a" }}</td></tr>
                <tr><td>Estimator</td><td class="num">{{ eq ? eq.estimator : "n/a" }}</td></tr>
                <tr><td>Sample</td><td class="num">{{ eq && eq.sampleStart ? eq.sampleStart : "n/a" }} → {{ eq && eq.sampleEnd ? eq.sampleEnd : "latest" }}</td></tr>
                <tr v-if="eq && eq.nSelected != null"><td>Non-zero regressors</td><td class="num">{{ eq.nSelected }} / {{ eq.coefficients.length }}</td></tr>
              </tbody>
            </table>
            <p class="lx-method mt-2">In-sample fit is not evidence of forecasting skill. {{ why("inSample") }}</p>
          </div>

          <!-- 8.2 Out-of-sample -->
          <div>
            <span class="kicker mb-2">Out-of-sample backtest</span>
            <div class="lx-toolbar pt-0 pb-2">
              <div class="lx-seg" role="tablist" aria-label="Horizon">
                <button v-for="h in horizons" :key="h" type="button" role="tab" class="lx-seg__btn" :class="{ 'lx-seg__btn--on': h === horizon }" :aria-selected="h === horizon" :disabled="!oosByH[h]" :title="oosByH[h] ? '' : 'No backtest at this horizon'" @click="horizon = h">{{ h }}w</button>
              </div>
            </div>
            <table class="lx-mtable" aria-label="Out-of-sample metrics">
              <thead><tr><th scope="col">Metric</th><th scope="col" class="num">h = {{ horizon }} weeks</th></tr></thead>
              <tbody>
                <tr v-for="k in oosKeys" :key="k">
                  <td>{{ help(k).label }}<span class="lx-help" :title="help(k).help" :aria-label="help(k).help" tabindex="0">?</span></td>
                  <td class="num">
                    <template v-if="oos && oos[k] != null">
                      <span v-if="k === 'theilURandomWalk' || k === 'theilUConsensus'"><span class="lx-dot" :class="dotClass(theilStatus(oos[k]))"></span>{{ fmtNum(oos[k], k) }} · {{ theilText(oos[k]) }}</span>
                      <span v-else-if="k === 'coverage80' || k === 'coverage95'">{{ fmtNum(oos[k], k) }} · {{ coverageText(k, oos[k]) }}</span>
                      <template v-else>{{ fmtNum(oos[k], k) }}</template>
                    </template>
                    <span v-else class="na" :title="whyOos(k)">n/a</span>
                  </td>
                </tr>
                <tr><td>Sample</td><td class="num"><template v-if="oos">n = {{ oos.n }} · {{ oos.vintages }} origin{{ oos.vintages === 1 ? "" : "s" }}<span v-if="oos.origin"> · {{ oos.origin }}</span></template><span v-else class="na">no backtest at this horizon</span></td></tr>
              </tbody>
            </table>
            <p v-if="oos" class="lx-method mt-2">{{ oos.method }}. Nominal interval coverage is a target, not an observed fact; realised coverage is reported only when an interval history exists.</p>
          </div>
        </div>

        <!-- Forecast vs realised -->
        <div class="mt-4">
          <span class="kicker mb-2">Forecast vs realised</span>
          <template v-if="vintagePoints.length >= 8">
            <svg :viewBox="`0 0 ${W} ${H}`" class="lx-mini" role="img" :aria-label="`Forecast versus realised at ${horizon} weeks over the last ${vintagePoints.length} origins`">
              <line :x1="pad" :y1="H - pad" :x2="W - pad" :y2="H - pad" stroke="var(--line)" />
              <polyline :points="poly(vintagePoints.map((p) => p.realised))" fill="none" stroke="var(--ser-hist)" stroke-width="1.5" />
              <polyline :points="poly(vintagePoints.map((p) => p.forecast))" fill="none" stroke="var(--ser-fcst)" stroke-width="1.5" stroke-dasharray="4 3" />
            </svg>
            <div class="lx-legend mt-1"><span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--hist"></span>Realised</span><span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--fcst"></span>Forecast ({{ horizon }}w ahead)</span></div>
          </template>
          <template v-else-if="window">
            <p class="lx-method">Fewer than eight forecast origins are available at this horizon, so the vintage chart is not drawn. The last out-of-sample window (origin {{ window.origin }}, {{ window.dates.length }} weeks) is shown instead.</p>
            <svg :viewBox="`0 0 ${W} ${H}`" class="lx-mini" role="img" :aria-label="`Last out-of-sample window for ${sel}: forecast versus realised`">
              <line :x1="pad" :y1="H - pad" :x2="W - pad" :y2="H - pad" stroke="var(--line)" />
              <polyline :points="poly(window.realised, windowRange)" fill="none" stroke="var(--ser-hist)" stroke-width="1.5" />
              <polyline :points="poly(window.forecast, windowRange)" fill="none" stroke="var(--ser-fcst)" stroke-width="1.5" stroke-dasharray="4 3" />
            </svg>
            <div class="lx-legend mt-1"><span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--hist"></span>Realised</span><span class="lx-legend__item"><span class="lx-legend__swatch lx-legend__swatch--fcst"></span>Out-of-sample forecast</span><span class="lx-method">{{ window.source }}</span></div>
          </template>
          <p v-else class="lx-method">No forecast-vs-realised history is available for this series.</p>
        </div>
      </template>
    </div>
  </section>
</template>

<script>
import { METRIC_HELP, theilStatus, coverageStatus } from "../lib/metrics";
import { formatNumber, formatValue } from "../lib/format";

export default {
  name: "ModelPerformance",
  props: {
    diagnostics: { type: Object, default: null },
    status: { type: String, default: "idle" },
    reason: { type: String, default: "" },
    series: { type: String, default: "" },
  },
  data: () => ({ open: true, sel: "", horizon: 13, horizons: [1, 4, 13, 26, 52], W: 520, H: 120, pad: 8 }),
  computed: {
    doc() {
      return this.diagnostics && this.diagnostics.schema ? this.diagnostics : null;
    },
    seriesList() {
      if (!this.doc) return [];
      const s = new Set();
      (this.doc.equations || []).forEach((e) => s.add(e.series));
      (this.doc.outOfSample || []).forEach((o) => s.add(o.series));
      return [...s];
    },
    eq() {
      return this.doc ? (this.doc.equations || []).find((e) => e.series === this.sel) || null : null;
    },
    inSample() {
      return (this.eq && this.eq.inSample) || {};
    },
    inSampleKeys: () => ["r2", "r2Adj", "regressionSe", "durbinWatson", "nObs"],
    oosKeys: () => ["rmse", "mae", "mape", "bias", "theilURandomWalk", "theilUConsensus", "hitRate", "coverage80", "coverage95"],
    oosByH() {
      const out = {};
      if (!this.doc) return out;
      for (const o of this.doc.outOfSample || []) if (o.series === this.sel) out[o.horizon] = o;
      return out;
    },
    oos() {
      return this.oosByH[this.horizon] || null;
    },
    vintagePoints() {
      if (!this.doc) return [];
      return (this.doc.vintages || []).filter((v) => v.series === this.sel && v.horizon === this.horizon).slice(-8);
    },
    window() {
      if (!this.doc) return null;
      return (this.doc.oosWindows || []).find((w) => w.series === this.sel) || null;
    },
    windowRange() {
      if (!this.window) return null;
      const all = [...this.window.realised, ...this.window.forecast].filter((v) => v != null);
      return [Math.min(...all), Math.max(...all)];
    },
  },
  watch: {
    seriesList: {
      immediate: true,
      handler(list) {
        if (!list.length) return;
        if (list.includes(this.series)) this.sel = this.series;
        else if (!list.includes(this.sel)) this.sel = list[0];
        this.pickHorizon();
      },
    },
    series(s) {
      if (this.seriesList.includes(s)) this.sel = s;
    },
    sel() {
      this.pickHorizon();
    },
  },
  methods: {
    theilStatus,
    pickHorizon() {
      if (!this.oosByH[this.horizon]) {
        const first = this.horizons.find((h) => this.oosByH[h]);
        if (first) this.horizon = first;
      }
    },
    help(k) {
      return METRIC_HELP[k] || { label: k, help: "" };
    },
    shortName(s) {
      return String(s).replace("Local stock market index: ", "").replace("Nominal exchange rate per USD", "Exchange rate").replace("5-Year CDS premium", "CDS");
    },
    fmtNum(v, k) {
      if (["hitRate", "coverage80", "coverage95", "mape"].includes(k)) return formatNumber(v, 1) + " %";
      if (["theilURandomWalk", "theilUConsensus", "r2", "r2Adj", "durbinWatson"].includes(k)) return formatNumber(v, 2);
      if (k === "nObs") return formatNumber(v, 0);
      return formatValue(this.sel, v);
    },
    why(block) {
      if (!this.doc || !this.doc.unavailable) return "";
      const u = this.doc.unavailable.find((x) => x.block === `${block}.${this.sel}`);
      return u ? u.reason : "";
    },
    whyOos(k) {
      if (!this.oos) return "No backtest at this horizon.";
      if (k === "mape") return "MAPE is not reported for this series (ratio-scale requirement or zero realised values).";
      if (k === "theilUConsensus") return "No market-consensus path exists for this series over the backtest window.";
      if (k.startsWith("coverage")) return this.why("outOfSample") || "No interval history to score coverage against.";
      return "Not provided.";
    },
    dotClass(s) {
      return { "lx-dot--good": s === "good", "lx-dot--warn": s === "similar", "lx-dot--bad": s === "bad" };
    },
    theilText(u) {
      const s = theilStatus(u);
      return s === "good" ? "beats no-change" : s === "similar" ? "similar to no-change" : s === "bad" ? "worse than no-change" : "";
    },
    coverageText(k, v) {
      const s = coverageStatus(k === "coverage80" ? 80 : 95, v);
      return s === "undercover" ? "under-covers (over-confident)" : s === "overcover" ? "over-covers" : "near nominal";
    },
    poly(values, range) {
      const vals = values.map(Number);
      const finite = vals.filter(Number.isFinite);
      if (!finite.length) return "";
      const [lo, hi] = range || [Math.min(...finite), Math.max(...finite)];
      const span = hi - lo || 1;
      const n = vals.length;
      return vals
        .map((v, i) => {
          if (!Number.isFinite(v)) return null;
          const x = this.pad + (i / Math.max(1, n - 1)) * (this.W - 2 * this.pad);
          const y = this.H - this.pad - ((v - lo) / span) * (this.H - 2 * this.pad);
          return `${x.toFixed(1)},${y.toFixed(1)}`;
        })
        .filter(Boolean)
        .join(" ");
    },
  },
};
</script>

<style scoped>
.lx-mini { width: 100%; max-width: 520px; height: 120px; display: block; background: var(--bg-1); border: 1px solid var(--line-soft); }
</style>
